import { exceedsFileSizeLimit } from 'features/shared/utility/fileSizeHelper';
import {
  hideAttachmentDialog,
  attachmentDialogSaved,
} from '../../../store/actions/attachmentDialog.actions';
import React, { useCallback, useMemo, useState } from 'react';
import { FILE_EXTENSION_REGEX, LEADING_DOT_REGEX } from 'features/shared/constants/regex';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useFormState } from 'react-hook-form';
import { Box, Grid, Typography } from '@material-ui/core';
import { getAttachmentIcon } from 'features/shared/utility/attachmentIconHelper';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import { RMSButton } from '../../buttons';
import RMSCommonDialog from '../../dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../dialog';
import { RMSTextField, RMSSelect, RMSCheckbox } from '../../form-controls';
import RMSFileUpload from '../../form-controls/RMSFileUpload/RMSFileUpload';
import RMSRecordHistoryButton from '../../modules/RMSRecordHistoryButton';
import RMSIconButton from '../../buttons/RMSIconButton';
import AttachmentCameraCapture from './AttachmentCameraCapture';
import AttachmentImagePreview from './AttachmentImagePreview';
import CreateFormResolver from '../../../utility/createFormResolver';
import { executeApiAction } from '../../../utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../utility/useConfirmDialog';
import { getDictionarySelectProps } from 'features/shared/utility/dictionarySelectHelper';
import { ATTACHMENT_DEFAULT_VALUES } from '../../../constants/attachmentFormConstants';
import {
  uploadAttachment,
  editAttachment,
  getAttachmentById,
} from '../../../services/attachmentService';
import { buildAttachmentSchema } from '../../../schema/attachmentSchema';
import {
  formatFileSize,
  splitFileName,
  downloadBufferAttachment,
} from '../../../utility/attachmentHelper';

const AddEditAttachmentDialog = ({ dialog }) => {
  const { parentId, parentType, canSave = true, attachment = null, requestId } = dialog;
  const open = true;
  const dispatch = useDispatch();
  const onClose = useCallback(() => dispatch(hideAttachmentDialog(requestId)), [
    dispatch,
    requestId,
  ]);
  const onSaved = useCallback(
    (savedAttachment) => {
      dispatch(attachmentDialogSaved(savedAttachment, requestId));
      dispatch(hideAttachmentDialog(requestId));
    },
    [dispatch, requestId]
  );
  const sizeLimit = useSelector((state) => state.attachments.sizelimit);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const isEditMode = Boolean(attachment?.ptsAttachmentID);
  const attachmentTypeOptions = useSelector((state) => state.dictionary?.codeAttachmentTypes);

  const methods = useForm({
    resolver: CreateFormResolver(buildAttachmentSchema(isEditMode)),
    mode: 'onSubmit',
    disabled: !canSave || submitting,
    defaultValues: isEditMode
      ? {
          FileName: splitFileName(attachment.FileName, attachment.FileType),
          Description: attachment.Description || '',
          codeAttachmentType: attachment.codeAttachmentType || '',
          IsDeleted: Boolean(attachment.IsDeleted),
        }
      : ATTACHMENT_DEFAULT_VALUES,
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const file = useMemo(() => pendingFiles[0]?.file ?? null, [pendingFiles]);

  const handleCameraCapture = useCallback(
    (capturedFile) => {
      if (exceedsFileSizeLimit(capturedFile, sizeLimit)) {
        dispatch(
          notify(`File size limit exceeded. Maximum allowed per file: ${sizeLimit} MB.`, 'error')
        );
        return;
      }

      const nextFiles = [
        {
          id: `${capturedFile.name}_${capturedFile.size}_${capturedFile.lastModified}`,
          file: capturedFile,
          title: capturedFile.name.replace(FILE_EXTENSION_REGEX, ''),
        },
      ];
      if (pendingFiles.length) {
        requestConfirm(
          'Using this photo will remove the previously selected file. Do you want to replace it?',
          () => setPendingFiles(nextFiles),
          'Replace File'
        );
      } else {
        setPendingFiles(nextFiles);
      }
    },
    [dispatch, sizeLimit, pendingFiles.length, requestConfirm]
  );

  const handleRemoveFile = useCallback(() => setPendingFiles([]), []);

  const handleDownload = useCallback(async () => {
    if (!attachment?.ptsAttachmentID || downloading) return;
    await executeApiAction({
      setLoading: setDownloading,
      dispatch,
      action: async () => {
        const response = await getAttachmentById(attachment.ptsAttachmentID);
        const fileObject = response?.[0]?.FileObject;
        if (fileObject == null) {
          throw new Error('The attachment file is unavailable for download.');
        }
        const bytes = fileObject?.data || fileObject;
        const buffer = Array.isArray(bytes) ? new Uint8Array(bytes) : bytes;
        const fileType = (attachment.FileType || '').replace(LEADING_DOT_REGEX, '');
        const fileName = attachment.FileName || 'attachment';
        const name =
          fileType && fileName.toLowerCase().endsWith(`.${fileType.toLowerCase()}`)
            ? splitFileName(fileName, fileType)
            : fileName;
        downloadBufferAttachment(buffer, fileType, name);
      },
    });
  }, [attachment, downloading, dispatch]);

  const onSubmit = useCallback(
    async (data) => {
      if (!canSave || submitting || (!isEditMode && (!file || !parentId || !parentType))) return;

      if (isEditMode) {
        const payload = {
          ptsAttachmentID: attachment.ptsAttachmentID,
          FileName: `${data.FileName}.${attachment.FileType}`,
          Description: data.Description,
          codeAttachmentType: data.codeAttachmentType,
          IsDeleted: data.IsDeleted,
        };

        await executeApiAction({
          setLoading: setSubmitting,
          dispatch,
          action: () => editAttachment(attachment.ptsAttachmentID, payload),
          onSuccess: (savedAttachment) => {
            dispatch(notify('Attachment updated successfully', 'success'));
            onSaved?.(savedAttachment);
          },
        });

        return;
      }

      const formData = new FormData();

      formData.append('parentType', parentType);
      formData.append('ptsParentID', parentId);
      formData.append('description', data.Description || '');
      formData.append('codeAttachmentType', data.codeAttachmentType || '');
      formData.append('fileName', file.name);
      formData.append('fileType', file.name.split('.').pop());
      formData.append('mimeType', file.type);
      formData.append('size', file.size);
      formData.append('file', file);

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () => uploadAttachment(formData),
        onSuccess: () => {
          dispatch(notify('Attachment uploaded successfully', 'success'));
          onSaved?.();
        },
      });
    },
    [isEditMode, attachment, parentId, parentType, file, dispatch, onSaved, canSave, submitting]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (submitting) return;
    if (isDirty || pendingFiles.length) {
      requestConfirm(
        'You have unsaved changes. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }
    onClose?.();
  }, [isDirty, pendingFiles.length, submitting, requestConfirm, onClose]);

  return (
    <>
      <RMSCommonDialog
        open={open}
        title={isEditMode ? 'Edit Attachment' : 'Add Attachment'}
        onClose={requestClose}
        onSave={handleSave}
        loading={submitting}
        saveDisabled={!canSave || submitting || (!isEditMode && !file)}
        maxWidth="sm"
        fullWidth
        closeOnBackdropClick={false}
        customButtons={
          isEditMode ? (
            <RMSButton
              label="Download"
              prefixIcon={<GetAppIcon />}
              onClick={handleDownload}
              disabled={downloading || submitting}
            />
          ) : (
            []
          )
        }
        leftActions={
          isEditMode && (
            <RMSRecordHistoryButton
              disabled={!attachment?.Created}
              created={attachment?.Created}
              createdBy={attachment?.CreatedBy}
              updated={attachment?.Updated}
              updatedBy={attachment?.UpdatedBy}
            />
          )
        }>
        <FormProvider {...methods}>
          {isEditMode && <AttachmentImagePreview attachment={attachment} />}
          <Grid container spacing={2}>
            {isEditMode ? (
              <Grid item xs={12}>
                <RMSTextField name="FileName" label="File Name" inputProps={{ maxLength: 255 }} />
              </Grid>
            ) : (
              <>
                <Grid item xs={12}>
                  <RMSFileUpload
                    showFileList={false}
                    files={pendingFiles}
                    onChange={setPendingFiles}
                    multiple={false}
                    maxFiles={1}
                    maxFileSize={sizeLimit}
                    accept="*"
                    subtitle={`Any file type is supported — up to ${sizeLimit} MB`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <AttachmentCameraCapture onCapture={handleCameraCapture} />
                </Grid>
                {file && (
                  <Grid item xs={12}>
                    <Box className="person-attachment-selected-file">
                      <img
                        alt=""
                        src={getAttachmentIcon(file.name.match(FILE_EXTENSION_REGEX)?.[0] || '')}
                        width={24}
                        height={24}
                      />
                      <Typography variant="body2" className="person-attachment-selected-file__name">
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ({formatFileSize(file.size)})
                      </Typography>
                      <RMSIconButton
                        icon={<CloseIcon fontSize="small" />}
                        tooltip="Remove"
                        onClick={handleRemoveFile}
                      />
                    </Box>
                  </Grid>
                )}
              </>
            )}
            <Grid item xs={12}>
              <RMSSelect
                name="codeAttachmentType"
                label="Type"
                options={attachmentTypeOptions || []}
                {...getDictionarySelectProps()}
              />
            </Grid>
            <Grid item xs={12}>
              <RMSTextField
                name="Description"
                label="Description"
                inputProps={{ maxLength: isEditMode ? 50 : 100 }}
              />
            </Grid>
            {isEditMode && (
              <Grid item xs={12}>
                <RMSCheckbox name="IsDeleted" label="Is Deleted" />
              </Grid>
            )}
          </Grid>

          {!isEditMode && !file && (
            <Typography variant="caption" color="textSecondary">
              Select a file above before saving.
            </Typography>
          )}
        </FormProvider>
      </RMSCommonDialog>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </>
  );
};

export default AddEditAttachmentDialog;
